import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useFormContext } from 'react-hook-form';
import { useBrand } from '../../../brands/BrandProvider';
import { useBrandRegistrationCopy } from '../../../brands/registrationCopy';
import PromotionalLinkWizard from './PromotionalLinkWizard';
import EditDiscountLinkDialog from './EditDiscountLinkDialog';
import EventQuickLinks from './EventQuickLinks';
import SavedLinkCard from './SavedLinkCard';
import {
  batchedLinkHasDiscount,
  enrichBatchedTicketLink,
  findBatchedPromoTarget,
  getBasePriceForBatchedLink,
  inferDiscountPercent,
  recalculatePromoPrice,
} from './linkEditUtils';
import { buildEventPageUrl, buildRegistrationUrl, resolveBatchedLinkDisplay, slugifyRef } from './paymentFormUtils';
import { eventService } from '../../../services/eventService';

interface BatchedTicketLinksPanelProps {
  slug: string;
}

type TicketLinkRow = {
  _id?: string;
  label?: string;
  refCode?: string;
  ticketTypeId?: string | null;
  ticketBatchId?: string | null;
  isActive?: boolean;
  promoTypeIndex?: number;
  hasDiscount?: boolean;
  discountPercent?: number;
  finalPrice?: number;
  baseTicketName?: string;
  baseTypeIndex?: number;
};

const BatchedTicketLinksPanel: React.FC<BatchedTicketLinksPanelProps> = ({ slug }) => {
  const { watch, setValue } = useFormContext();
  const brand = useBrand();
  const t = useBrandRegistrationCopy();
  const eventId = watch('_id') as string | undefined;
  const ticketTypes = (watch('ticketTypes') || []) as Array<{
    name?: string;
    hiddenFromPublicPage?: boolean;
    isActive?: boolean;
    batches?: Array<{ _id?: string; name?: string; price?: number }>;
  }>;
  const ticketLinks: TicketLinkRow[] = watch('ticketLinks') || [];

  const [loaded, setLoaded] = useState(false);
  const [editLinkIndex, setEditLinkIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!eventId || loaded || !ticketTypes.length) return;
    eventService.getTicketLinks(eventId).then((res) => {
      if (res.success && Array.isArray(res.data)) {
        setValue(
          'ticketLinks',
          res.data.map((link) => enrichBatchedTicketLink(link, ticketTypes))
        );
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [eventId, loaded, setValue, ticketTypes.length]);

  const updateLink = (index: number, patch: Record<string, unknown>) => {
    const next = [...ticketLinks];
    next[index] = { ...next[index], ...patch };
    setValue('ticketLinks', next, { shouldDirty: true });
  };

  const removeLink = (index: number) => {
    setValue(
      'ticketLinks',
      ticketLinks.filter((_, i) => i !== index),
      { shouldDirty: true }
    );
  };

  const handleSaveLink = (values: { label: string; refCode: string; discountPercent?: number }) => {
    if (editLinkIndex == null) return;
    const link = ticketLinks[editLinkIndex];
    const hasDiscount = batchedLinkHasDiscount(link, ticketTypes);

    if (hasDiscount && values.discountPercent != null) {
      const target = findBatchedPromoTarget(link, ticketTypes);
      const basePrice = getBasePriceForBatchedLink(link, ticketTypes);
      if (!target || basePrice == null) return;

      const finalPrice = recalculatePromoPrice(basePrice, values.discountPercent);
      const types = [...ticketTypes];
      const promoType = types[target.typeIndex];
      if (promoType?.batches?.[target.batchIndex]) {
        promoType.batches[target.batchIndex] = {
          ...promoType.batches[target.batchIndex],
          price: finalPrice,
          name: values.label,
        };
        if (link.baseTicketName) {
          promoType.name = `${link.baseTicketName} — ${values.label}`;
        }
        setValue('ticketTypes', types, { shouldDirty: true });
      }

      const batchId = promoType?.batches?.[target.batchIndex]?._id;
      updateLink(editLinkIndex, {
        label: values.label,
        refCode: values.refCode,
        discountPercent: values.discountPercent,
        finalPrice,
        hasDiscount: true,
        ticketBatchId: batchId || link.ticketBatchId,
        promoTypeIndex: target.typeIndex,
      });
    } else {
      updateLink(editLinkIndex, {
        label: values.label,
        refCode: values.refCode,
      });
    }

    setEditLinkIndex(null);
  };

  const handleDeleteLink = (index: number) => {
    const link = ticketLinks[index];
    const target = findBatchedPromoTarget(link, ticketTypes);
    if (target) {
      setValue(
        'ticketTypes',
        ticketTypes.filter((_, i) => i !== target.typeIndex),
        { shouldDirty: true }
      );
    }
    removeLink(index);
    if (editLinkIndex === index) setEditLinkIndex(null);
  };

  const confirmDeleteLink = (index: number) => {
    if (
      window.confirm(
        t('Excluir este link? O link deixará de funcionar. Inscrições já feitas não são afetadas.')
      )
    ) {
      handleDeleteLink(index);
    }
  };

  const editingLinkRaw = editLinkIndex != null ? ticketLinks[editLinkIndex] : null;
  const editingLink = editingLinkRaw ? enrichBatchedTicketLink(editingLinkRaw, ticketTypes) : null;
  const editingHasDiscount = editingLink ? batchedLinkHasDiscount(editingLink, ticketTypes) : false;
  const editingTarget = editingLink ? findBatchedPromoTarget(editingLink, ticketTypes) : null;
  const editingBasePrice = editingLink ? getBasePriceForBatchedLink(editingLink, ticketTypes) : null;
  const editingBatchPrice =
    editingTarget != null
      ? Number(ticketTypes[editingTarget.typeIndex]?.batches?.[editingTarget.batchIndex]?.price)
      : null;

  const resolveUrl = (link: TicketLinkRow): string => {
    if (!slug || !link.refCode?.trim()) return '';
    const ref = slugifyRef(link.refCode);
    const brandKey = brand.key;

    if (link.ticketBatchId) {
      return buildRegistrationUrl(slug, { ticketId: link.ticketBatchId, ref, brandKey });
    }

    if (link.promoTypeIndex != null) {
      const batch = ticketTypes[link.promoTypeIndex]?.batches?.[0];
      if (batch?._id) {
        return buildRegistrationUrl(slug, { ticketId: batch._id, ref, brandKey });
      }
    }

    const matchedHidden = ticketTypes.find(
      (type) => type.hiddenFromPublicPage && type.batches?.[0]?.name === link.label
    );
    if (matchedHidden?.batches?.[0]?._id) {
      return buildRegistrationUrl(slug, {
        ticketId: matchedHidden.batches[0]._id,
        ref,
        brandKey,
      });
    }

    return buildRegistrationUrl(slug, { ref, brandKey });
  };

  return (
    <Stack spacing={2.5}>
      <Typography variant="body2" color="text.secondary">
        Crie links nomeados para WhatsApp, Instagram ou grupos. Marque desconto só se quiser preço diferente.
      </Typography>

      <PromotionalLinkWizard mode="batched" slug={slug} />

      {!slug && (
        <Alert severity="info">
          Informe o endereço (slug) do evento na etapa de informações básicas e salve para gerar os links copiáveis.
        </Alert>
      )}

      {!eventId && slug && (
        <Alert severity="info">
          Salve o evento para registrar os links no servidor.
        </Alert>
      )}

      {slug && (
        <>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Seus links personalizados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Links que você criou acima — com nome, preço e código de origem.
            </Typography>

            {ticketLinks.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: 'grey.50', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhum link ainda. Preencha o formulário acima e clique em &quot;Criar link&quot;.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {ticketLinks.map((link, index) => {
                  const info = resolveBatchedLinkDisplay(link, ticketTypes);
                  return (
                    <SavedLinkCard
                      key={link._id || index}
                      label={link.label?.trim() || `Link ${index + 1}`}
                      url={resolveUrl(link)}
                      refCode={link.refCode}
                      info={info}
                      onEdit={() => setEditLinkIndex(index)}
                      onDelete={() => confirmDeleteLink(index)}
                    />
                  );
                })}
              </Stack>
            )}
          </Box>

          <EventQuickLinks
            eventPageUrl={buildEventPageUrl(slug, brand.key)}
            registrationUrl={buildRegistrationUrl(slug, { brandKey: brand.key })}
          />
        </>
      )}

      <EditDiscountLinkDialog
        open={editLinkIndex != null}
        onClose={() => setEditLinkIndex(null)}
        onSave={handleSaveLink}
        onDelete={editLinkIndex != null ? () => handleDeleteLink(editLinkIndex) : undefined}
        hasDiscount={editingHasDiscount}
        initialLabel={editingLink?.label || ''}
        initialRefCode={editingLink?.refCode || ''}
        initialDiscountPercent={
          editingLink?.discountPercent ??
          (editingBasePrice && (editingLink?.finalPrice ?? editingBatchPrice)
            ? inferDiscountPercent(editingBasePrice, editingLink?.finalPrice ?? editingBatchPrice ?? 0) ?? 10
            : 10)
        }
        baseTicketName={editingLink?.baseTicketName || 'Ingresso'}
        basePrice={editingBasePrice}
      />
    </Stack>
  );
};

export default BatchedTicketLinksPanel;
